import React from 'react';
import { IReviewsGeneral, IStarRating } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Stars } from '@/components/resort/Stars';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { ChoiceBadge } from '@/components/common/ChoiceBadge/ChoiceBadge';
import { cn } from '@/util/cn';
import { I18nKey } from '@/i18n/types';

// Aggregate-rating card on the Resort Reviews page. Renders:
//   1. Stat row — Visitors' Choice badge + overall-rating helper text
//      on the left; "Total (X.X)" label + 5 stars on the right.
//   2. Category breakdown — up to 8 star-rated dimensions
//      (beginner / intermediate / expert / all-mountain terrain on the
//      left, family-friendly / après-ski / terrain park / overall value
//      on the right). Categories with no rating are hidden; the columns
//      rebalance to keep both sides full.
//
// The "Rate this resort" CTA now lives in a dedicated WriteReviewCallout
// directly below this card on the page — kept out of this component so
// the card stays purely a stat surface.

interface IProps {
  reviewsGeneral: IReviewsGeneral;
  className?: string;
}

const CATEGORY_KEYS: { field: keyof IStarRating; labelKey: I18nKey['common'] }[] = [
  { field: 'beginner', labelKey: 'region.resortGuide.beginnerTitle' },
  { field: 'intermediate', labelKey: 'region.resortGuide.intermediateTitle' },
  { field: 'expert', labelKey: 'region.resortGuide.expertTitle' },
  { field: 'allTerrain', labelKey: 'region.resortGuide.allMtnTerrainTitle' },
  { field: 'familyFriendly', labelKey: 'region.resortGuide.familyFriendlyTitle' },
  { field: 'apresSki', labelKey: 'region.resortGuide.apresSkiTitle' },
  { field: 'terrainPark', labelKey: 'region.resortGuide.terrainParkTitle' },
  { field: 'overallOther', labelKey: 'resort.reviews.overallValue' },
];

const starsSmClass = '[&_svg]:h-4 [&_svg]:w-4 [&_svg]:mr-1 [&_svg:last-child]:mr-0';
const starsMdClass = '[&_svg]:h-5 [&_svg]:w-5 [&_svg]:mr-1 [&_svg:last-child]:mr-0';

const CategoryRow = ({ label, value }: { label: string; value: number }) => (
  <li className="flex items-center justify-between gap-4 border-b border-border py-3 last:border-b-0">
    <span className="text-xs font-bold uppercase tracking-wider text-foreground">{label}</span>
    <Stars amount={value} className={cn('flex items-center', starsSmClass)} />
  </li>
);

export const GeneralReviews = ({ reviewsGeneral, className }: IProps) => {
  const { t } = useOTSTranslation('common');
  const locale = getLocale() as ELocale;

  const activeCategories = CATEGORY_KEYS
    .map((c) => ({ ...c, value: reviewsGeneral.generalRating[c.field] }))
    .filter((c) => !!c.value);
  const half = Math.ceil(activeCategories.length / 2);
  const left = activeCategories.slice(0, half);
  const right = activeCategories.slice(half);

  const { overall } = reviewsGeneral.generalRating;

  return (
    <section className={cn('rounded-md border border-border bg-card', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b border-border px-5 py-5">
        <ChoiceBadge locale={locale} className="order-1 h-12 w-auto md:order-none" />
        {/* On mobile the helper copy drops to its own full-width row (order-3 + w-full);
            on md+ it sits inline between the badge and the rating, capped at max-w-md. */}
        <p className="order-3 m-0 w-full text-sm leading-relaxed text-muted-foreground md:order-none md:w-auto md:max-w-md md:flex-1">
          {t('resort.reviews.OverallRatingHelpText')}
        </p>
        <div className="order-2 flex items-center gap-3 md:order-none">
          <span className="text-sm font-semibold text-foreground">
            {overall
              ? `${t('resort.reviews.total')} (${overall.toFixed(1)})`
              : t('resort.reviews.total')}
          </span>
          <Stars amount={overall} className={cn('flex items-center', starsMdClass)} />
        </div>
      </div>

      {!!activeCategories.length && (
        <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x md:divide-border">
          <ul className="m-0 list-none px-5">
            {left.map((c) => (
              <CategoryRow key={c.field} label={t(c.labelKey)} value={c.value!} />
            ))}
          </ul>
          {!!right.length && (
            <ul className="m-0 list-none px-5">
              {right.map((c) => (
                <CategoryRow key={c.field} label={t(c.labelKey)} value={c.value!} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
